import { useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";

/**
 * Returns a `fetch` wrapper that attaches the current Clerk session token as
 * an `Authorization: Bearer <token>` header, for calling API endpoints that
 * are guarded by `requireClerkAuth` (corpus-api and indexing-worker).
 *
 * Usage:
 *   const authFetch = useAuthFetch();
 *   const res = await authFetch(`${API_BASE}/query/search`, { method: "POST", ... });
 */
export function useAuthFetch(): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  const { getToken } = useAuth();

  return useCallback(
    async (input: RequestInfo | URL, init: RequestInit = {}) => {
      const token = await getToken();
      const headers = new Headers(init.headers);
      if (token && !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return fetch(input, { ...init, headers });
    },
    [getToken],
  );
}
